/**
 * Schedule View — the gear popover's form model and its save payload.
 *
 * ⛔ KEEP THIS FILE IMPORT-FREE (the node-tests execute it verbatim).
 *
 * ── WHAT LIVES HERE AND WHAT DELIBERATELY DOES NOT ───────────────────────────────────────────
 * Here: the ORDER the alerts are listed in, their labels and icons, their prerequisites, and the
 * rules for turning the form back into a storable blob.
 *
 * NOT here: the DEFAULTS. There is exactly one definition of what "absent" means and it is
 * schedule-view-service/lib/settings.js. `/schedule-view/day` returns the RESOLVED settings and
 * this form starts from those. A second copy would drift the first time one of them changed, and
 * the symptom is "the office swears they turned that off" — unfalsifiable from a bug report.
 *
 * ── WHY SAVING IS GUARDED ────────────────────────────────────────────────────────────────────
 * The blob is per OFFICE, not per user: one careless save changes what everybody in the practice
 * sees. So `buildSettingsPayload` refuses to produce a payload it cannot stand behind — no site,
 * settings never loaded, a value out of range, or a blob that does not read back as written.
 * Mirrors the shape of src/utils/aiRecallCadence.js `buildSavePayload`, for the same reason.
 *
 * ⚠️ KNOWN LIMIT: the page saves exactly the keys the server resolved. A key the RESOLVER does not
 * know about was already stripped before the page ever saw it, so it cannot be preserved here.
 * That is coherent only because the resolver is the single definition — any new setting is added
 * THERE first. Adding a key to this file alone would silently drop it on the next save.
 */

/** teamio.settings Property that holds the blob. Must match lib/settings.js SETTINGS_PROPERTY. */
export const SETTINGS_PROPERTY = 'SCHEDULE_VIEW_SETTINGS';

/**
 * The eleven alerts, in the order the popover lists them.
 *
 * ⛔ This list and its ORDER must match schedule-view-service/lib/settings.js ALERT_KEYS. A key
 * here that the resolver does not know is dropped on save; a key there that is missing here is
 * invisible in the popover and can never be turned off. Pinned by
 * cypress/node-tests/schedule-view-settings.test.js against the real backend file.
 */
export const ALERT_META = [
  { key: 'recall', icon: 'fa-history', label: 'Overdue for recall' },
  { key: 'unsched', icon: 'fa-wrench', label: 'Hygiene visit with unscheduled treatment' },
  { key: 'balance', icon: 'fa-dollar', label: 'Outstanding balance' },
  { key: 'task', icon: 'fa-tasks', label: 'Tagged in an open Teamio task' },
  { key: 'followup', icon: 'fa-bell-o', label: 'Open Teamio follow-up' },
  { key: 'claim', icon: 'fa-file-text-o', label: 'Claim sent over 30 days ago' },
  { key: 'premed', icon: 'fa-medkit', label: 'Requires premedication' },
  { key: 'medhx', icon: 'fa-heartbeat', label: 'Medical history update due' },
  { key: 'forms', icon: 'fa-wpforms', label: 'Pending form or packet' },
  { key: 'txsign', icon: 'fa-pencil-square-o', label: 'Treatment plan awaiting signature' },
  { key: 'insverify', icon: 'fa-shield', label: 'Insurance needs verification' },
];

export const ALERT_KEYS = ALERT_META.map(a => a.key);

/**
 * The three alerts that cannot be computed without another feature being set up.
 *
 * The server decides whether a site HAS each one (`capabilities` in the /day payload); this is
 * only the sentence shown next to the greyed-out checkbox. Saying "unavailable" without saying
 * what unlocks it is the difference between a hint and a dead end.
 */
export const ALERT_PREREQUISITES = {
  medhx: 'Needs Form Update Reminders set up, with an interval and at least one form.',
  txsign: 'Needs Tx Presenter enabled for this office.',
  insverify: 'Needs Insurance Verification enabled for this office.',
};

export const STATUS_BUCKETS = ['confirmed', 'unconfirmed', 'arrived', 'completed'];

export const BUCKET_LABELS = {
  confirmed: 'Confirmed',
  unconfirmed: 'Unconfirmed',
  arrived: 'Arrived',
  completed: 'Completed',
};

/**
 * Largest blob we will write. `teamio.settings.PropertyValue` is a TEXT column, so this is not a
 * truncation guard — it is a sanity cap, so a runaway confirmation-bucket map cannot quietly grow
 * into something nobody can read back. Same figure as the AI-recall blob.
 */
export const MAX_BLOB_BYTES = 4000;

/** Highest balance minimum the form will accept. Above this it is a typo, not a policy. */
export const MAX_BALANCE_MINIMUM = 100000;

/** A plain deep copy for JSON-shaped data, so editing the form never mutates the store. */
function clone(value) {
  if (value === null || typeof value !== 'object') return value;
  if (Array.isArray(value)) return value.map(clone);
  const out = {};
  Object.keys(value).forEach((k) => { out[k] = clone(value[k]); });
  return out;
}

/** Structural equality for JSON-shaped data. Used to prove a blob reads back as written. */
function sameShape(a, b) {
  if (a === b) return true;
  if (a === null || b === null) return false;
  if (typeof a !== typeof b) return false;
  if (typeof a !== 'object') return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const ka = Object.keys(a);
  const kb = Object.keys(b);
  if (ka.length !== kb.length) return false;
  return ka.every(k => Object.prototype.hasOwnProperty.call(b, k) && sameShape(a[k], b[k]));
}

/**
 * The editable form for one office's settings.
 *
 * Returns null when there are no resolved settings yet — the popover must not open on a form full
 * of invented values, because saving it would overwrite the office's real configuration with
 * something nobody chose.
 *
 * @param {Object|null} settings  the RESOLVED settings from /schedule-view/day
 */
export function formFromSettings(settings) {
  if (!settings || typeof settings !== 'object') return null;

  const alerts = {};
  ALERT_KEYS.forEach((key) => {
    alerts[key] = (settings.alerts && settings.alerts[key] !== undefined)
      ? !!settings.alerts[key]
      : true;
  });

  return {
    alerts,
    showProcedures: !!settings.showProcedures,
    showConfirmation: !!settings.showConfirmation,
    showBlockouts: !!settings.showBlockouts,
    showBrokenAppointments: !!settings.showBrokenAppointments,
    // A string, because it is bound to a text input. Coerced (and validated) at save time.
    balanceMinimum: String(Number(settings.balanceMinimum) || 0),
    confirmationBuckets: clone(settings.confirmationBuckets || {}),
  };
}

/** Has anything actually changed? Nothing is written for a popover that was only looked at. */
export function isDirty(form, settings) {
  const original = formFromSettings(settings);
  if (!original || !form) return false;
  return !sameShape(form, original);
}

/**
 * Turn the form into a `customer/upsertSettings` payload, or refuse with reasons.
 *
 * @param {Object} form
 * @param {Object} opts  { siteId, settingsLoaded }
 * @returns {{ok: true, siteId: string, settings: Object} | {ok: false, errors: string[]}}
 */
export function buildSettingsPayload(form, opts) {
  const o = opts || {};

  // (1) CROSS-SITE SAFETY. teamio.settings is unique on (SiteID, Property), so a site-less payload
  //     either 400s at the API or writes against the wrong office. The form must not be able to
  //     produce one at all.
  const siteId = typeof o.siteId === 'string' ? o.siteId.trim() : '';
  if (!siteId) {
    return { ok: false, errors: ['No office is selected. Reopen this window and try again.'] };
  }

  // (2) Never write values over a configuration we have not read. The form's starting values come
  //     from the server; if that never arrived, they are not this office's settings.
  if (o.settingsLoaded === false || !form || typeof form !== 'object') {
    return {
      ok: false,
      errors: ["This office's settings haven't loaded yet. Close this and try again."],
    };
  }

  const minimum = Number(String(form.balanceMinimum === undefined ? 0 : form.balanceMinimum).trim());
  if (!Number.isFinite(minimum) || minimum < 0) {
    return { ok: false, errors: ['Enter a balance minimum of 0 or more.'] };
  }
  if (minimum > MAX_BALANCE_MINIMUM) {
    return { ok: false, errors: [`Enter a balance minimum under $${MAX_BALANCE_MINIMUM}.`] };
  }

  const alerts = {};
  ALERT_KEYS.forEach((key) => { alerts[key] = !!(form.alerts && form.alerts[key]); });

  // Only real bucket names survive. A typo'd bucket would reach the grid as a status with no
  // colour at all — and the server's resolver would drop it anyway, so the office would see their
  // choice silently revert.
  const confirmationBuckets = {};
  const src = form.confirmationBuckets;
  if (src && typeof src === 'object' && !Array.isArray(src)) {
    Object.keys(src).forEach((defNum) => {
      if (STATUS_BUCKETS.indexOf(src[defNum]) !== -1) confirmationBuckets[String(defNum)] = src[defNum];
    });
  }

  const blob = {
    alerts,
    showProcedures: !!form.showProcedures,
    showConfirmation: !!form.showConfirmation,
    showBlockouts: !!form.showBlockouts,
    showBrokenAppointments: !!form.showBrokenAppointments,
    balanceMinimum: minimum,
    confirmationBuckets,
  };

  const serialized = JSON.stringify(blob);
  if (serialized.length > MAX_BLOB_BYTES) {
    return {
      ok: false,
      errors: ['These settings are too large to save. Reset the confirmation status mapping and try again.'],
    };
  }

  // (3) Prove it reads back. A UI bug that produced unreadable JSON would otherwise put the whole
  //     office on default behaviour, and the resolver would hide it by never throwing.
  let roundTrip;
  try {
    roundTrip = JSON.parse(serialized);
  } catch (e) {
    roundTrip = null;
  }
  if (!sameShape(roundTrip, blob)) {
    return { ok: false, errors: ['Could not save these settings safely. Please try again.'] };
  }

  return {
    ok: true,
    // Echoed back so the caller dispatches with the SAME site this payload was built for — it
    // cannot drift if somebody switched office while the popover was open.
    siteId,
    settings: { [SETTINGS_PROPERTY]: serialized },
  };
}
